// Import core lib dependencies.
import React 				from 'react';
import PropTypes from 'prop-types';
import { connect } 			from 'react-redux';

// Import child components elements.
import DateGoal				from './types/DateGoal.jsx';
import NumberGoal			from './types/NumberGoal.jsx';
import DateNumberGoal		from './types/DateNumberGoal.jsx';

// Actions.
import Actions 				from '../actions.js';

// Selectors.
import Selectors 			from '../selectors.js';

// Other component dependencies.
import CaptchaInterface	 	from '../../common/Captcha/index.js';
import OverlayInterface		from '../../common/Overlay/index.js';
import { Header }			from '../../../common/components/displayItems/document.jsx';

// Misc dependencices.
import { capitalize, getLabel } 		from '../../../common/utils/string.js';
import { AnalyticsEvents as PUB } from '../../../common/config/api.js';

// Config.
import Config				from '../config/config.js';
import Enums				from '../config/enums.js';

// Model objects.
import { Entity } 			from '../model.js';

/**
 * <pre>
 * Goal module React class.
 *
 * Summary of responsibilities:
 *
 *  - smart component (connected to Redux store).
 *  - outputs markup to render the outer/wrapper Goal elements.
 *  - composes the correponding child Goal type component (Date, DateNumber, Number) based on received props.
 *  - handles events received from child components and calls mapped dispatch functions as required.
 *  - composes other common components eg. Overlay, Captcha when required.
 *
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Goal.Components
 * @namespace
 *
 * @return {module:Goal.Components.Goal}
 */
class Goal extends React.Component {

  /**
     * Constructor.
     *
     * @param {object} props The injected props
     */
  constructor(props) {

    super(props);

    // Bind methods.
    this.checkState 					= this.checkState.bind(this);
    this.checkProgress 					= this.checkProgress.bind(this);
    this.participate 					= this.participate.bind(this);

    this.handleParticipate 				= this.handleParticipate.bind(this);
    this.handleCaptchaResponse 			= this.handleCaptchaResponse.bind(this);
    this.handleCloseCaptchaOverlay 		= this.handleCloseCaptchaOverlay.bind(this);
    this.handleCloseConfirmationOverlay = this.handleCloseConfirmationOverlay.bind(this);
    this.handleCloseErrorOverlay 		= this.handleCloseErrorOverlay.bind(this);

    // Buffer.
    this.buffer = {};
  }

  getChildContext() {
    return { config: this.props.config };
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  componentDidMount() {
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
     * Render the component. Compose child components as required.
     *
     * @return {module:Goal.Components.Goal}
     */
  render() {

    // Define common props and event handlers for injection into Goal type components.
    const _commonGoalTypeProps = {
      metricId: 		 	 this.props.metricId,
      type: 				     this.props.type,
      strategies: 	 	this.props.strategies,
      dates: 			 	   this.props.dates,
      templateOpts: 	 	this.props.templateOpts,
      milestones: 	 	this.props.milestones,
      labels: 		 	   this.props.labels,
      formatting: 		 this.props.formatting,
      eventHandlers: {
        checkState: 	  this.checkState,
        checkProgress: 	this.checkProgress,
        participate: 	 this.handleParticipate
      },
      state: 				this.props.state,
      progress: 			this.props.progress
    };

    // Pass off rendering bulk of component to appropriate render method based on Goal type.
    let _goalType;
    switch (this.props.type) {

      // Just has an end date target (see how many participants we can get before the end date).
      case Enums.GOAL_TYPE_DATE:
        _goalType = this.renderDateType(_commonGoalTypeProps);
        break;

        // Number target (no end date, can take as long as required to reach goal).
      case Enums.GOAL_TYPE_NUMBER:
        _goalType = this.renderNumberType(_commonGoalTypeProps);
        break;

        // A number and a date target (see if we can reach the target before the end date).
      case Enums.GOAL_TYPE_DATENUMBER:
        _goalType = this.renderDateNumberType(_commonGoalTypeProps);
        break;

        // Unknown.
      default:
        _goalType = this.renderUnknownType();
        break;
    }

    // Captcha overlay.
    let _captchaOverlay;
    if (this.props.showCaptcha) {
      _captchaOverlay = this.renderCaptcha(this.handleCloseCaptchaOverlay, this.handleCaptchaResponse);
    }

    // Confirmation overlay.
    let _confirmationOverlay;
    if (this.props.showConfirmation) {
      _confirmationOverlay = this.renderOverlay(this.handleCloseConfirmationOverlay, this.props.labels.thanksForParticipating);
    }

    // Error message overlay.
    let _errorOverlay;
    if (this.props.showError) {
      _errorOverlay = this.renderOverlay(this.handleCloseErrorOverlay, this.props.errorMessage);
    }

    return (

      <div ref="componentInner" className="xComponentInner">

        <Header item={this.props.moduleItem} />

        {_goalType}

        {_captchaOverlay}
        {_confirmationOverlay}
        {_errorOverlay}

      </div>
    );
  }

  /**
	 * <pre>
     * Render a date Goal type.
     *
     * - bar without segments
     * - progress bar indicates time progression to end date
     * - show total number of votes received somewhere
     * </pre>
     *
     * @param {object} commonGoalTypeProps Object of prop types common to all Goal types.
     * @return {module:Goal.Components.Types.DateGoal}
     */
  renderDateType(commonGoalTypeProps) {

    // Define target for this Goal type.
    const _target = this.props.targets.date;

    return  <DateGoal {...commonGoalTypeProps}
						  target={_target} />;
  }

  /**
     * <pre>
     * Render a number Goal type.
     *
     * - bar with or without segments (display purposes only)
     * - progress indicates number of votes received
     * </pre>
     *
     * @param {object} commonGoalTypeProps Object of prop types common to all Goal types.
     * @return {module:Goal.Components.Types.NumberGoal}
     */
  renderNumberType(commonGoalTypeProps) {

    // Define target for this Goal type.
    const _target = this.props.targets.numeric;

    return  <NumberGoal {...commonGoalTypeProps}
						    target={_target} />;
  }

  /**
	 * <pre>
     * Render a date + number Goal type.
     *
     * - bar with segments (gated by date)
     * - progress indicates number of votes received
     * </pre>
     *
     * @param {object} commonGoalTypeProps Object of prop types common to all Goal types.
     * @return {module:Goal.Components.Types.DateNumberGoal}
     */
  renderDateNumberType(commonGoalTypeProps) {

    // Define target for this Goal type.
    const _targets = this.props.targets;

    return  <DateNumberGoal {...commonGoalTypeProps}
						    	targets={_targets} />;
  }

  /**
     * Render an unknown Goal type (output an error).
     *
     * @return {ReactElement}
     */
  renderUnknownType() {

    return (

      <p className="xCopy xError">[UNKNOWN GOAL TYPE!]</p>
    );
  }

  /**
	 * Render an Overlay component.
	 *
	 * @param {function} closeHandler The handler method to be called when overlay is closed.
	 * @param {string} message The message to output inside the overlay.
	 * @return {module:Overlay.Components.Overlay}
	 */
  renderOverlay(closeHandler, message) {

    let _overlay;

    // Do not attempt to render an Overlay without a message.
    if (message) {
      _overlay =  <OverlayInterface.Components.Overlay closeHandler={closeHandler} classes="xGoalMessage">
        <div className="xCopyContainer">
          <p className="xCopy">{message}</p>
        </div>
      </OverlayInterface.Components.Overlay>;
    }

    return _overlay;
  }

  /**
	 * Render a Captcha component inside an Overlay.
	 *
	 * @param {function} closeHandler The handler method to be called when overlay is closed.
	 * @param {function} responseHandler The handler method to be called when Captcha is completed.
	 * @return {module:Captcha.Components.Captcha}
	 */
  renderCaptcha(closeHandler, responseHandler) {

    const _overlay =  <OverlayInterface.Components.Overlay closeHandler={closeHandler}>
      <CaptchaInterface.Components.Captcha responseHandler={responseHandler} />
    </OverlayInterface.Components.Overlay>;

    return _overlay;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
	 * Handler called when participate button is clicked.
	 *
	 * @param {object} event The click event object.
	 * @param {object} goalTypeArgs Any additional specific arguments that the child goal type has returned.
	 */
  handleParticipate(event, goalTypeArgs) {

    // Buffer any arguments that need to be passed through to the participate call once captcha is complete.
    this.buffer.goalTypeArgs = goalTypeArgs;

    // Behave according to fraud prevention strategy.
    switch (this.props.strategies.fraud) {

      // Present Captcha overlay.
      case Enums.GOAL_FRAUD_STRATEGY_CAPTCHA:

        // Dispatch action to show captcha.
        this.props.dispatch.showCaptcha(this.props.id);

        break;

        // Otherwise allow the participate request to submit.
      case Enums.GOAL_FRAUD_STRATEGY_DYNCAPTCHA:
      case Enums.GOAL_FRAUD_STRATEGY_NONE:
      default:

        this.participate(goalTypeArgs);

        break;
    }
  }

  /**
	 * Handler called when captcha has been completed.
	 *
	 * @param {object} response The Captcha response object.
	 */
  handleCaptchaResponse(response) {

    // Allow the participate request to submit.
    this.participate(this.buffer.goalTypeArgs, response);

    // Close Recaptcha overlay.
    this.handleCloseCaptchaOverlay(true);
  }

  /**
	 * Handler called when captcha overlay is closed.
	 *
	 * @param {boolean} valid Boolean indicating if the Captcha was completed successfully or aborted.
	 */
  handleCloseCaptchaOverlay(valid) {

    // Dispatch action to hide confirmation overlay.
    this.props.dispatch.hideCaptcha(this.props.id);

    // Clear buffered arguments.
    delete this.buffer.goalTypeArgs;

    // If user closed Recaptcha overlay without completing it.
    if (!valid) {

      // Nothing required here yet.
    }
  }

  /**
	 * Handler called when confirmation overlay is closed.
	 *
	 * @param {object} event The click event object.
	 */
  handleCloseConfirmationOverlay(event) {

    // Dispatch action to hide confirmation overlay.
    this.props.dispatch.hideConfirmation(this.props.id);
  }

  /**
	 * Handler called when error overlay is closed.
	 *
	 * @param {object} event The click event object.
	 */
  handleCloseErrorOverlay(event) {

    // Dispatch action to hide error overlay.
    this.props.dispatch.hideError(this.props.id);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Action dispatcher proxy methods */

  /**
	 * Dispatch action to check goal state.
	 *
	 * @param {object} goalTypeArgs Any additional specific arguments that the child goal type has returned.
	 */
  checkState(goalTypeArgs) {

    // Dispatch action.
    this.props.dispatch.checkState(this.props.id, this.props.metricId, this.props.strategies, this.props.dates, this.props.labels, this.props.type, goalTypeArgs);
  }

  /**
	 * Dispatch action to check goal progress.
	 *
	 * @param {object} goalTypeArgs Any additional specific arguments that the child goal type has returned.
	 */
  checkProgress(goalTypeArgs) {

    // Define additional params unique to this action.
    const params = {};

    // Dispatch action.
    this.props.dispatch.checkProgress(this.props.id, this.props.metricId, this.props.dates, this.props.targets, params, this.props.type, goalTypeArgs);
  }

  /**
	 * Dispatch action to participate.
	 *
	 * @param {object} goalTypeArgs Any additional specific arguments that the child goal type has returned.
	 * @param {object} captchaResponse The Captcha response object.
	 */
  participate(goalTypeArgs, captchaResponse=null) {

    // Define additional params unique to this action.
    const params = {};

    // Add captcha response param if supplied.
    if (captchaResponse) {
      params['g-captcha-response'] = captchaResponse;
    }

    if (this.props.triggerEvent) {
      // Notify API of user interacts with the goal action
      this.props.triggerEvent(PUB.ACTION_GOAL);
    }

    // Dispatch action.
    this.props.dispatch.participate(this.props.id, this.props.metricId, this.props.strategies, this.props.dates, this.props.targets, params, this.props.type, goalTypeArgs);
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} id The module id
 * @property {Number} metricId The metric id
 * @property {String} type The metric type
 * @property {module:Goal.Model.Entity.Strategies} strategies Object defining the various strategies in use (eg. for milestones, fraud prevention, overflow)
 * @property {module:Goal.Model.Entity.Dates} dates Object defining the start and end dates for the metric
 * @property {module:Goal.Model.Entity.Targets} targets Object defining the targets for the metric (numeric or datetime based on metric type)
 * @property {(Array<module:Goal.Model.Entity.Milestone>|Array<module:Goal.Model.Entity.GatedMilestone>)} milestones Array containing the milestone definition objects
 * @property {module:Goal.Model.Entity.TemplateOptions} templateOpts Object defining the various module template options
 * @property {Object} labels Object defining a k/v set of localized labels
 * @property {module:Goal.Model.Entity.Formatting} formatting Object defining a various formatting options (eg. no of decimal places to show progress percentages)
 * @property {Object} moduleItem Object containing modules item content (eg. heading, byline etc).
 */
Goal.propTypes = {
  id: 			       PropTypes.string,
  metricId: 		  PropTypes.number,
  type: 			     PropTypes.string,
  strategies: 	 PropTypes.instanceOf(Entity.Strategies),
  dates: 			    PropTypes.instanceOf(Entity.Dates),
  targets: 		   PropTypes.instanceOf(Entity.Targets),
  milestones: 	 PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Entity.Milestone), PropTypes.instanceOf(Entity.GatedMilestone)])),
  templateOpts: 	PropTypes.instanceOf(Entity.TemplateOptions),
  labels: 		    PropTypes.object,
  formatting: 	 PropTypes.instanceOf(Entity.Formatting),
  moduleItem: 	 PropTypes.object
};

/**
 * React default props.
 *
 * @type {Object}
 */
Goal.defaultProps = {

};

Goal.childContextTypes = {
  config: PropTypes.object,
};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = (state, props) => {

  return {

    state: 				Selectors.getState(state, props.id),
    progress: 			Selectors.getProgress(state, props.id),

    showConfirmation: 	Selectors.getShowConfirmation(state, props.id),
    showError: 			    Selectors.getShowError(state, props.id),
    errorMessage: 		  Selectors.getErrorMessage(state, props.id),

    showCaptcha: 		Selectors.getShowCaptcha(state, props.id)
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = (dispatch) => {

  return {

    dispatch: {

      checkState: function (id, metricId, strategies, dates, labels, goalType, goalTypeArgs) {
        dispatch( Actions.checkState(id, metricId, strategies, dates, labels, goalType, goalTypeArgs) );
      },

      checkProgress: function (id, metricId, dates, targets, params, goalType, goalTypeArgs) {
        dispatch( Actions.checkProgress(id, metricId, dates, targets, params, goalType, goalTypeArgs) );
      },

      participate: function (id, metricId, strategies, dates, targets, params, goalType, goalTypeArgs) {
        dispatch( Actions.participate(id, metricId, strategies, dates, targets, params, goalType, goalTypeArgs) );
      },

      showCaptcha: function (id) {
        dispatch( Actions.showCaptcha(id) );
      },

      hideCaptcha: function (id) {
        dispatch( Actions.hideCaptcha(id) );
      },

      hideConfirmation: function (id) {
        dispatch( Actions.hideConfirmation(id) );
      },

      hideError: function (id) {
        dispatch( Actions.hideError(id) );
      }
    }
  };
};

// Generate connected/smart component.
const GoalConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(Goal);

// Export the connected/smart component as default.
export default GoalConnected;

// Also export the dumb component (for unit testing, doc preview etc).
export { Goal as docPreviewExport };
